import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  FiUser,
  FiMail,
  FiLock,
  FiShield,
  FiUserPlus,
  FiCheck,
  FiX,
  FiInfo,
  FiFileText,
  FiCamera,
  FiUploadCloud
} from 'react-icons/fi';
import { useApi } from '../../hooks/useApi';
import { useAuthStore } from '../../stores';
import { ErrorDisplay } from '../Common/ErrorDisplay';
import { SimpleFormField } from '../Common/FormField';
import TermsModal from './TermsModal';

interface FormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface TermsData {
  signature: string;
  printedName: string;
  date: string;
  email: string;
}

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser, setToken } = useAuthStore();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState<string>('');
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsData, setTermsData] = useState<TermsData | null>(null);
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const passwordFieldRef = useRef<HTMLDivElement>(null);
  const requirementsRef = useRef<HTMLDivElement>(null);
  const referralIdFromUrl = String(searchParams.get('ref') || '').trim();

  useEffect(() => {
    if (/^[a-f\d]{24}$/i.test(referralIdFromUrl)) {
      localStorage.setItem('referralId', referralIdFromUrl);
    }
  }, [referralIdFromUrl]);

  // Password requirements validation
  const passwordRequirements = {
    minLength: formData.password.length >= 8,
    hasUpperCase: /[A-Z]/.test(formData.password),
    hasLowerCase: /[a-z]/.test(formData.password),
    hasNumber: /[0-9]/.test(formData.password),
    hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password),
  };

  const allRequirementsMet = Object.values(passwordRequirements).every(Boolean);

  // Close requirements tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        requirementsRef.current &&
        passwordFieldRef.current &&
        !requirementsRef.current.contains(event.target as Node) &&
        !passwordFieldRef.current.contains(event.target as Node)
      ) {
        setShowPasswordRequirements(false);
      }
    };

    if (showPasswordRequirements) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showPasswordRequirements]);

  const registerMutation = useApi.useRegister({
    onSuccess: (data) => {
      if (data.token && data.user) {
        localStorage.removeItem('referralId');
        setToken(data.token);
        setUser(data.user);
        if (data.user.role === 'master_pending') {
          navigate('/register/pending');
        } else {
          navigate('/dashboard');
        }
      }
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      setProfileImage(file);
      setImagePreview(URL.createObjectURL(file));
      setError('');
    }
  };

  const handleTermsAccept = (data: TermsData) => {
    setTermsData(data);
    setTermsAccepted(true);
    setShowTermsModal(false);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }
    if (!formData.password) {
      setError('Password is required');
      return;
    }

    // Check password requirements
    if (!passwordRequirements.minLength) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!passwordRequirements.hasUpperCase) {
      setError('Password must contain at least one uppercase letter');
      return;
    }
    if (!passwordRequirements.hasLowerCase) {
      setError('Password must contain at least one lowercase letter');
      return;
    }
    if (!passwordRequirements.hasNumber) {
      setError('Password must contain at least one number');
      return;
    }
    if (!passwordRequirements.hasSpecialChar) {
      setError('Password must contain at least one special character');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Check terms acceptance
    if (!termsAccepted || !termsData) {
      setError('You must accept the Terms & Conditions to continue');
      return;
    }

    // Register as follower (default role) with terms data
    const formDataToSend = new FormData();
    formDataToSend.append('name', formData.name);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('password', formData.password);
    formDataToSend.append('role', 'follower');
    formDataToSend.append('termsAccepted', 'true');
    formDataToSend.append('termsSignature', termsData.signature);
    formDataToSend.append('termsPrintedName', termsData.printedName);
    formDataToSend.append('termsEmail', termsData.email);

    const storedReferralId = String(localStorage.getItem('referralId') || '').trim();
    if (/^[a-f\d]{24}$/i.test(storedReferralId)) {
      formDataToSend.append('uplineId', storedReferralId);
    }

    if (profileImage) {
      formDataToSend.append('profileImage', profileImage);
    }

    registerMutation.mutate(formDataToSend);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0e1016] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
          <img src="/trade-pilot-logo.png" alt="Trade Pilot" className="lg:h-[120px] lg:w-[120px] h-[110px] w-[110px] object-cover" />
          </div>
          <h2 className="mt-0 lg:mt-0 md:text-4xl text-2xl font-extrabold title-brand-gradient">Trade Pilot</h2>
          <p className="mt-2 text-md text-gray-600 dark:text-muted">Create Your Account</p>
          <p className="mt-1 text-sm text-gray-500">Start copy trading in minutes</p>
        </div>

        <div className="card">
          {error && <ErrorDisplay error={error} onDismiss={() => setError('')} className="mb-4" />}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Profile Image Upload */}
            <div className="flex flex-col items-center justify-center mb-6">
              <div className="relative group">
                <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-lg bg-gray-100 flex items-center justify-center">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <FiUser className="w-12 h-12 text-gray-400" />
                  )}
                </div>
                <label
                  htmlFor="profile-upload"
                  className="absolute bottom-0 right-0 bg-primary-500 p-2 rounded-full text-white cursor-pointer shadow-md hover:bg-primary-600 transition-colors"
                >
                  <FiCamera className="w-4 h-4" />
                  <input
                    id="profile-upload"
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-2">Upload Profile Image (Optional)</p>
            </div>

            <SimpleFormField
              label="Full Name"
              name="name"
              icon={<FiUser className="h-5 w-5 text-gray-400" />}
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="John Doe"
            />

            <SimpleFormField
              label="Email Address"
              name="email"
              type="email"
              icon={<FiMail className="h-5 w-5 text-gray-400" />}
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="john@example.com"
            />

            <div ref={passwordFieldRef} className="space-y-2">
              <SimpleFormField
                label="Password"
                name="password"
                type="password"
                icon={<FiLock className="h-5 w-5 text-gray-400" />}
                value={formData.password}
                onChange={handleChange}
                onFocus={() => setShowPasswordRequirements(true)}
                required
                placeholder="••••••••"
                showPasswordToggle={true}
              />

              {/* Password Requirements — in-flow so it does not cover Confirm Password */}
              {showPasswordRequirements && (
                <div
                  ref={requirementsRef}
                  className="relative z-10 w-full rounded-lg border border-border bg-surface-elevated p-4 shadow-sm transition-all duration-200"
                >
                  <div className="mb-3 flex items-center gap-2">
                    <FiInfo className="h-5 w-5 text-primary" />
                    <h3 className="text-sm font-semibold text-foreground">Password Requirements</h3>
                  </div>
                  <ul className="space-y-2">
                    <li className={`flex items-center gap-2 text-sm ${passwordRequirements.minLength ? 'text-green-600' : 'text-muted'}`}>
                      {passwordRequirements.minLength ? (
                        <FiCheck className="h-4 w-4 text-green-600" />
                      ) : (
                        <FiX className="h-4 w-4 text-muted" />
                      )}
                      <span>At least 8 characters</span>
                    </li>
                    <li className={`flex items-center gap-2 text-sm ${passwordRequirements.hasUpperCase ? 'text-green-600' : 'text-muted'}`}>
                      {passwordRequirements.hasUpperCase ? (
                        <FiCheck className="h-4 w-4 text-green-600" />
                      ) : (
                        <FiX className="h-4 w-4 text-muted" />
                      )}
                      <span>One uppercase letter (A-Z)</span>
                    </li>
                    <li className={`flex items-center gap-2 text-sm ${passwordRequirements.hasLowerCase ? 'text-green-600' : 'text-muted'}`}>
                      {passwordRequirements.hasLowerCase ? (
                        <FiCheck className="h-4 w-4 text-green-600" />
                      ) : (
                        <FiX className="h-4 w-4 text-muted" />
                      )}
                      <span>One lowercase letter (a-z)</span>
                    </li>
                    <li className={`flex items-center gap-2 text-sm ${passwordRequirements.hasNumber ? 'text-green-600' : 'text-muted'}`}>
                      {passwordRequirements.hasNumber ? (
                        <FiCheck className="h-4 w-4 text-green-600" />
                      ) : (
                        <FiX className="h-4 w-4 text-muted" />
                      )}
                      <span>One number (0-9)</span>
                    </li>
                    <li className={`flex items-center gap-2 text-sm ${passwordRequirements.hasSpecialChar ? 'text-green-600' : 'text-muted'}`}>
                      {passwordRequirements.hasSpecialChar ? (
                        <FiCheck className="h-4 w-4 text-green-600" />
                      ) : (
                        <FiX className="h-4 w-4 text-muted" />
                      )}
                      <span>One special character (!@#$%^&*...)</span>
                    </li>
                  </ul>
                </div>
              )}
            </div>

            <SimpleFormField
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              icon={<FiShield className="h-5 w-5 text-gray-400" />}
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              placeholder="••••••••"
              showPasswordToggle={true}
            />

            {/* Terms and Conditions */}
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="terms"
                  checked={termsAccepted}
                  readOnly
                  className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 cursor-pointer"
                />
                <label htmlFor="terms" className="text-sm text-gray-700 flex-1">
                  I have read and agree to the{' '}
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(true)}
                    className="text-primary-600 hover:text-primary-700 font-semibold underline"
                  >
                    Terms & Conditions
                  </button>
                </label>
              </div>

              {termsAccepted && termsData && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                    <FiCheck className="h-4 w-4" />
                    <span className="font-semibold">Terms Accepted</span>
                  </div>
                  <p className="text-xs text-green-600 dark:text-green-500 mt-1">
                    Signed by {termsData.printedName} on {termsData.date}
                  </p>
                </div>
              )}

              {!termsAccepted && (
                <button
                  type="button"
                  onClick={() => setShowTermsModal(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-primary-500 text-primary-600 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 font-semibold transition-colors"
                >
                  <FiFileText className="h-5 w-5" />
                  <span>Read & Accept Terms</span>
                </button>
              )}
            </div>

            <button
              type="submit"
              disabled={registerMutation.isPending || !termsAccepted}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {registerMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                  <span>Creating account...</span>
                </>
              ) : (
                <>
                  <FiUserPlus className="h-5 w-5" />
                  <span>Create Account</span>
                </>
              )}
            </button>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                Already have an account?{' '}
                <Link to="/login" className="font-semibold text-primary-600 hover:text-primary-700 transition-colors">
                  Sign in
                </Link>
              </p>
            </div>
            {/* <div className="text-center">
              <p className="text-lg font-semibold text-gray-600">
                <Link to="/become-master-trader" className="font-semibold text-primary-600 hover:text-primary-700 transition-colors">
                Become Master Trader{' '}?{' '}
                </Link>
              </p>
            </div> */}
          </form>
        </div>
      </div>

      {/* Terms Modal */}
      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={handleTermsAccept}
        email={formData.email}
      />
    </div>
  );
};

export default Register;
